// src/components/CustomTable.tsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Check, Settings } from 'lucide-react';

interface CustomTableProps {
  id: string;
  initialData?: string[][];
  headers?: boolean;
  bordered?: boolean;
  striped?: boolean;
  onSave: (id: string, html: string) => void;
  onDelete: (id: string) => void;
  initialHtml?: string;
  onTableDataChange?: (id: string, data: string[][]) => void;
}

export function CustomTable({ 
  id, 
  initialData = [['', '', '', ''], ['', '', ''], ['', '', '', ''], ['', '', '']], 
  headers = true, 
  bordered = true, 
  striped = false, 
  onSave,
  onDelete,
  initialHtml,
  onTableDataChange
}: CustomTableProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tableData, setTableData] = useState<string[][]>(initialData);
  const [hasHeaders, setHasHeaders] = useState(headers);
  const [isBordered, setIsBordered] = useState(bordered);
  const [isStriped, setIsStriped] = useState(striped);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Parse initial HTML if provided
  useEffect(() => {
    if (initialHtml) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(initialHtml, 'text/html');
        const table = doc.querySelector('table');
        
        if (table) {
          const rows = Array.from(table.querySelectorAll('tr'));
          const data = rows.map(row => {
            const cells = Array.from(row.querySelectorAll('th, td'));
            return cells.map(cell => cell.textContent || '');
          });
          
          if (data.length > 0) {
            setTableData(data);
            setHasHeaders(!!table.querySelector('thead'));
            setIsBordered(table.classList.contains('bordered') || 
                        table.classList.contains('bordered'));
            setIsStriped(table.classList.contains('striped'));
          }
        }
      } catch (error) {
        console.error('Error parsing table HTML:', error);
      }
    }
  }, [initialHtml]);

  // Notify parent component when table data changes
  useEffect(() => {
    if (onTableDataChange) {
      onTableDataChange(id, tableData);
    }
  }, [tableData, id, onTableDataChange]);

  const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
    
    // Notify parent component if callback is provided
    if (onTableDataChange) {
      onTableDataChange(id, newData);
    }
  };

  const addRow = () => {
    const newRow = Array(tableData[0]?.length || 3).fill('');
    setTableData([...tableData, newRow]);
  };

  const addColumn = () => {
    const newData = tableData.map(row => [...row, '']);
    setTableData(newData);
  };

  const removeRow = (index: number) => {
    if (tableData.length <= 1) return;
    const newData = tableData.filter((_, i) => i !== index);
    setTableData(newData);
  };

  const removeColumn = (index: number) => {
    if (tableData[0]?.length <= 1) return;
    const newData = tableData.map(row => row.filter((_, i) => i !== index));
    setTableData(newData);
  };

  const handleSave = () => {
    setIsEditing(false);
    
    // Generate HTML representation with proper styling
    let html = `<div class="custom-table-container" data-table-id="${id}" data-headers="${hasHeaders}" data-bordered="${isBordered}" data-striped="${isStriped}">`;
    html += '<table class="custom-table">';
    
    // Add headers if enabled
    if (hasHeaders && tableData.length > 0) {
      html += '<thead><tr>';
      for (let i = 0; i < (tableData[0]?.length || 0); i++) {
        html += `<th>${tableData[0][i] || `Header ${i + 1}`}</th>`;
      }
      html += '</tr></thead>';
    }
    
    // Add body
    html += '<tbody>';
    const startRow = hasHeaders ? 1 : 0;
    for (let i = startRow; i < tableData.length; i++) {
      html += '<tr>';
      for (let j = 0; j < (tableData[i]?.length || 0); j++) {
        html += `<td>${tableData[i][j] || ''}</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody></table></div>';
    
    onSave(id, html);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    onDelete(id);
  };

  const handleToggleHeaders = () => {
    setHasHeaders(!hasHeaders);
  };

  const handleToggleBordered = () => {
    setIsBordered(!isBordered);
  };

  const handleToggleStriped = () => {
    setIsStriped(!isStriped);
  };

  const handleToggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  if (isEditing) {
    return (
      <div className="border-2 border-blue-500 rounded-lg p-4 my-4 bg-blue-50">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Modifier le tableau</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="p-1 rounded bg-green-500 text-white hover:bg-green-600"
            >
              <Check className="h-4 w-4 mr-2" />
            </button>
            <button
              onClick={handleDelete}
              className="p-1 rounded bg-red-500 text-white hover:bg-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`headers-${id}`}
              checked={hasHeaders}
              onChange={(e) => {
                setHasHeaders(e.target.checked);
                // Auto-save when settings change
                setTimeout(() => {
                  let html = `<div class="custom-table-container" data-table-id="${id}" data-headers="${hasHeaders}" data-bordered="${isBordered}" data-striped="${isStriped}">`;
                  html += `<table class="custom-table ${isBordered ? 'border border-gray-300' : ''} ${isStriped ? 'striped' : ''}">`;
                  
                  if (hasHeaders && tableData.length > 0) {
                    html += '<thead><tr>';
                    for (let i = 0; i < (tableData[0]?.length || 0); i++) {
                      html += `<th>${tableData[0][i] || `Header ${i + 1}`}</th>`;
                    }
                    html += '</tr></thead>';
                  }
                  html += '<tbody>';
                  const startRow = hasHeaders ? 1 : 0;
                  for (let i = startRow; i < tableData.length; i++) {
                    html += `<tr>`;
                    for (let j = 0; j < (tableData[i]?.length || 0); j++) {
                      html += `<td>${tableData[i][j] || ''}</td>`;
                    }
                    html += '</tr>';
                  }
                  html += '</tbody></table></div>';
                  
                  onSave(id, html);
                }, 100);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 my-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Tableau</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleEdit}
            className="p-1 rounded bg-blue-500 text-white hover:bg-blue-600"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={handleDelete}
            className="p-1 rounded bg-red-500 text-white hover:bg-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          {hasHeaders && (
            <thead>
              <tr>
                {tableData[0]?.map((header, index) => (
                  <th key={index} className="border border-gray-300 px-4 py-2 bg-gray-100">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {(hasHeaders ? tableData.slice(1) : tableData).map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border border-gray-300 px-4 py-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}